import { createCommandIcon } from "./editorCommandPresentation.mjs";
import {
  captureScrollOrigin,
  getScrollAncestors,
  readViewportRect,
  rectIntersectsViewport,
  translateRectByScroll
} from "./scrollDelta.mjs";

function validRange(value) {
  try {
    return Number.isInteger(value?.from)
      && Number.isInteger(value?.to)
      && value.from >= 0
      && value.to > value.from;
  } catch {
    return false;
  }
}

let generatedMenuId = 0;

export function createSlashMenuController({
  element,
  ariaOwner = null,
  execute = () => false,
  onPosition = () => {},
  viewport
} = {}) {
  if (!element) throw new TypeError("slash menu element is required");
  if (!element.id) element.id = `slash-menu-${++generatedMenuId}`;
  const document = element.ownerDocument;
  const window = document?.defaultView || globalThis.window;
  const readViewport = typeof viewport === "function"
    ? viewport
    : () => document.documentElement.getBoundingClientRect();
  let commands = [];
  let range = null;
  let caretRect = null;
  let activeIndex = 0;
  let open = false;
  let disposed = false;
  let scrollOrigin = captureScrollOrigin(window, element);
  let observedScrollAncestors = new Set();

  function syncAncestorScrollListeners() {
    const next = new Set(getScrollAncestors(element));
    for (const ancestor of observedScrollAncestors) {
      if (!next.has(ancestor)) ancestor.removeEventListener("scroll", reposition);
    }
    for (const ancestor of next) {
      if (!observedScrollAncestors.has(ancestor)) ancestor.addEventListener("scroll", reposition);
    }
    observedScrollAncestors = next;
  }

  function close() {
    open = false;
    element.hidden = true;
    element.removeAttribute("aria-activedescendant");
    ariaOwner?.removeAttribute?.("aria-controls");
    ariaOwner?.removeAttribute?.("aria-activedescendant");
    ariaOwner?.setAttribute?.("aria-expanded", "false");
  }

  function invoke(command) {
    if (disposed || !open || !command || !range) return false;
    const captured = { from: range.from, to: range.to };
    try { execute(command.id, captured); } catch {}
    close();
    return true;
  }

  function render() {
    if (disposed || !open || commands.length === 0) return close();
    const anchor = translateRectByScroll(caretRect, scrollOrigin, window);
    const view = readViewportRect(readViewport);
    if (!rectIntersectsViewport(anchor, view)) return close();
    const options = commands.map((command, index) => {
      const option = document.createElement("button");
      option.type = "button";
      option.id = `slash-option-${index}`;
      option.dataset.command = command.id;
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", String(index === activeIndex));
      option.className = "slash-option";
      const icon = document.createElement("span");
      icon.className = "slash-option__icon";
      icon.append(createCommandIcon(document, command.id));
      const label = document.createElement("span");
      label.className = "slash-option__label";
      label.textContent = command.label || command.shortLabel || command.id;
      const shortcut = document.createElement("span");
      shortcut.className = "slash-option__shortcut";
      shortcut.textContent = index === activeIndex ? "Enter" : "";
      option.append(icon, label, shortcut);
      return option;
    });
    element.replaceChildren(...options);
    element.hidden = false;
    element.setAttribute("aria-activedescendant", options[activeIndex].id);
    ariaOwner?.setAttribute?.("aria-controls", element.id);
    ariaOwner?.setAttribute?.("aria-expanded", "true");
    ariaOwner?.setAttribute?.("aria-activedescendant", options[activeIndex].id);
    try { onPosition(element, anchor); } catch {}
  }

  function update(next) {
    if (disposed) return;
    let nextCommands;
    try { nextCommands = Array.isArray(next?.commands) ? next.commands.filter((command) => typeof command?.id === "string") : []; }
    catch { nextCommands = []; }
    if (!validRange(next) || nextCommands.length === 0) {
      commands = [];
      range = null;
      caretRect = null;
      close();
      return;
    }
    commands = nextCommands;
    range = { from: next.from, to: next.to };
    caretRect = next.caretRect;
    syncAncestorScrollListeners();
    scrollOrigin = captureScrollOrigin(window, element);
    activeIndex = 0;
    open = true;
    render();
  }

  function handleKeyDown(event) {
    if (disposed || !open) return false;
    if (event?.key === "Escape") {
      event.preventDefault?.();
      close();
      return true;
    }
    if (event?.key === "ArrowDown" || event?.key === "ArrowUp") {
      event.preventDefault?.();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      activeIndex = (activeIndex + delta + commands.length) % commands.length;
      render();
      return true;
    }
    if (event?.key === "Enter" && commands[activeIndex]) {
      event.preventDefault?.();
      return invoke(commands[activeIndex]);
    }
    return false;
  }

  function handlePointerDown(event) {
    if (event.target?.closest?.('[role="option"]')) event.preventDefault();
  }

  function handleClick(event) {
    const option = event.target?.closest?.('[role="option"]');
    if (!option || !element.contains(option)) return;
    const index = Array.from(element.querySelectorAll('[role="option"]')).indexOf(option);
    if (index >= 0 && commands[index]) invoke(commands[index]);
  }

  function reposition() {
    if (!disposed && open) {
      const anchor = translateRectByScroll(caretRect, scrollOrigin, window);
      if (!rectIntersectsViewport(anchor, readViewportRect(readViewport))) return close();
      try { onPosition(element, anchor); } catch {}
    }
  }

  function destroy() {
    if (disposed) return;
    close();
    disposed = true;
    commands = [];
    range = null;
    caretRect = null;
    ariaOwner?.removeAttribute?.("aria-controls");
    ariaOwner?.removeAttribute?.("aria-activedescendant");
    ariaOwner?.removeAttribute?.("aria-expanded");
    element.removeEventListener("pointerdown", handlePointerDown);
    element.removeEventListener("click", handleClick);
    window?.removeEventListener?.("scroll", reposition);
    window?.removeEventListener?.("resize", reposition);
    for (const ancestor of observedScrollAncestors) ancestor.removeEventListener("scroll", reposition);
    observedScrollAncestors.clear();
  }

  element.addEventListener("pointerdown", handlePointerDown);
  element.addEventListener("click", handleClick);
  window?.addEventListener?.("scroll", reposition);
  window?.addEventListener?.("resize", reposition);
  syncAncestorScrollListeners();

  return { update, close, destroy, handleKeyDown, isOpen: () => open && !disposed };
}
